import React, { useState, useEffect, useRef } from 'react';
import { Rocket, ChevronUp, ChevronDown, X, Check } from 'lucide-react';

import { useTranslation } from 'react-i18next';

export interface OnboardingStepAction {
  prompt?: string;
  url?: string;
  newWindow?: boolean;
}

export interface OnboardingStep {
  id: string;
  label: string;
  status: 'completed' | 'current' | 'pending';
  action?: OnboardingStepAction;
  isDismissible?: boolean;
}

interface OnboardingProgressStepsProps {
  steps: OnboardingStep[];
  onStepAction?: (step: OnboardingStep) => void;
  onDismiss?: (step: OnboardingStep) => void;
  className?: string;
}

export const OnboardingProgressSteps: React.FC<
  OnboardingProgressStepsProps
> = ({ steps, onStepAction, onDismiss, className = '' }) => {
  const { t } = useTranslation('pleri/chat');
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate progress percentage
  const completedSteps = steps.filter(
    (step) => step.status === 'completed',
  ).length;
  const totalSteps = steps.length;
  const percentage =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDismissPanel = () => {
    setIsExpanded(false);
  };

  const handleStepAction = (step: OnboardingStep) => {
    // Close the dropdown when action is triggered
    setIsExpanded(false);
    onStepAction?.(step);
  };

  // Handle ESC key press to close dropdown
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isExpanded]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isExpanded
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // Don't render if no steps or if 100% complete
  if (steps.length === 0 || percentage === 100) {
    return null;
  }

  return (
    <div ref={containerRef} className={`relative mt-2 ${className}`}>
      {/* Compact Progress Widget */}
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 rounded-full border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-2 transition-all hover:shadow-md dark:border-gray-600 dark:from-gray-800 dark:to-gray-800"
        aria-expanded={isExpanded}
        aria-label={t('onboardingProgress')}
      >
        <Rocket className="h-4 w-4 text-orange-500" />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('iStillNeedAFewThingsFrom')}
        </span>
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {percentage}%
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        )}
      </button>
      {/* Slide Down Panel - Absolute positioned */}
      <div
        className={`absolute top-full left-1/2 z-30 mt-2 -translate-x-1/2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl transition-all duration-300 ease-in-out dark:border-gray-700 dark:bg-gray-800 ${
          isExpanded
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-4 opacity-0'
        }`}
        style={{ width: '480px' }}
      >
        <div className="p-4">
          {/* Header with Dismiss */}
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-orange-500" />
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('completeYourSetup')}
              </span>
            </div>
            <button
              onClick={handleDismissPanel}
              className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label={t('closePanel')}
            >
              <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Steps List */}
          <div className="space-y-2">
            {/* To-Dos (Pending and Current) */}
            {steps
              .filter((step) => step.status !== 'completed')
              .map((step) => {
                const isCurrent = step.status === 'current';

                return (
                  <div
                    key={step.id}
                    className={`flex items-center justify-between gap-3 rounded-lg p-3 transition-all ${
                      isCurrent
                        ? 'border border-orange-200 bg-orange-50 dark:border-gray-600 dark:bg-gray-800'
                        : 'border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-700'
                    }`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                          isCurrent
                            ? 'border-orange-500'
                            : 'border-gray-400 dark:border-gray-500'
                        }`}
                      />
                      <div className="flex min-w-0 flex-1 items-center">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {step.label}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {onDismiss && step.isDismissible && (
                        <button
                          onClick={() => onDismiss(step)}
                          className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                          aria-label={t('dismiss')}
                        >
                          <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        </button>
                      )}
                      {onStepAction && step.action && (
                        <button
                          onClick={() => handleStepAction(step)}
                          className="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold whitespace-nowrap text-white transition-colors hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-500"
                        >
                          {t('doItNow')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

            {/* Completed Section */}
            {steps.filter((step) => step.status === 'completed').length > 0 && (
              <>
                <div className="mt-4 mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('completed')}
                  </span>
                </div>
                {steps
                  .filter((step) => step.status === 'completed')
                  .map((step) => (
                    <div
                      key={step.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-orange-50/30 p-3 transition-all dark:border-gray-700 dark:bg-gray-800/20"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-500 text-white">
                          <Check className="h-4 w-4" />
                        </div>
                        <div className="flex min-w-0 flex-1 items-center">
                          <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {step.label}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
